export type CyclePhase = 'follicular' | 'ovulatory' | 'luteal' | 'menstrual'

export type CycleProfile = {
  sex: string | null
  last_period_start_date: string | null
  avg_cycle_length: number
  avg_period_duration: number
}

export function isFemaleProfile(sex: string | null | undefined): boolean {
  return String(sex ?? '').toUpperCase().startsWith('F')
}

export function getCycleDay(profile: CycleProfile, date = new Date()): number | null {
  if (!profile.last_period_start_date) return null
  const start = new Date(`${profile.last_period_start_date}T12:00:00`)
  const diff = Math.floor((date.getTime() - start.getTime()) / 86400000)
  if (diff < 0) return null
  return (diff % profile.avg_cycle_length) + 1
}

export function getCyclePhase(profile: CycleProfile, date = new Date()): CyclePhase | null {
  const day = getCycleDay(profile, date)
  if (day === null) return null

  const periodEnd = profile.avg_period_duration
  if (day <= periodEnd) return 'menstrual'
  if (day <= 13) return 'follicular'
  if (day <= 16) return 'ovulatory'
  return 'luteal'
}

export function isLateLuteal(profile: CycleProfile, date = new Date()): boolean {
  const day = getCycleDay(profile, date)
  if (day === null) return false
  return day >= 21 && day <= profile.avg_cycle_length
}

export function shouldReduceTrainingVolume(profile: CycleProfile, date = new Date()): boolean {
  const phase = getCyclePhase(profile, date)
  return phase === 'luteal' || phase === 'menstrual'
}

export function trainingVolumeModifier(profile: CycleProfile, date = new Date()): number {
  if (!isFemaleProfile(profile.sex)) return 1
  if (!shouldReduceTrainingVolume(profile, date)) return 1
  const phase = getCyclePhase(profile, date)
  return phase === 'menstrual' ? 0.85 : 0.9
}

export type CyclePhaseSegment = {
  phase: CyclePhase
  startDay: number
  endDay: number
}

export function getCyclePhaseSegments(profile: CycleProfile): CyclePhaseSegment[] {
  const periodEnd = profile.avg_period_duration
  const cycleLen = profile.avg_cycle_length
  return [
    { phase: 'menstrual', startDay: 1, endDay: periodEnd },
    { phase: 'follicular', startDay: periodEnd + 1, endDay: 13 },
    { phase: 'ovulatory', startDay: 14, endDay: 16 },
    { phase: 'luteal', startDay: 17, endDay: cycleLen },
  ]
}

export function getCycleProgress(profile: CycleProfile, date = new Date()): {
  day: number | null
  cycleLength: number
  progressPct: number
} {
  const day = getCycleDay(profile, date)
  const cycleLength = profile.avg_cycle_length
  if (day === null) {
    return { day: null, cycleLength, progressPct: 0 }
  }
  return {
    day,
    cycleLength,
    progressPct: Math.min(100, Math.max(0, ((day - 0.5) / cycleLength) * 100)),
  }
}

export function daysUntilNextPeriod(profile: CycleProfile, date = new Date()): number | null {
  const day = getCycleDay(profile, date)
  if (day === null) return null
  return profile.avg_cycle_length - day + 1
}
